import React from 'react';
import { LockClosedIcon, ScaleIcon } from '@heroicons/react/24/outline';

import {
  Badge,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
} from '../../components/ui';
import { approvalGates } from './data';

const coveredItems = [
  ['Legal contingencies', 'ASC 450', 'GC assessment', 'Accrue if probable and estimable; disclose if reasonably possible.'],
  ['Warrant liability MTM', 'ASC 480', 'Tech Accounting', 'Quarterly remeasurement through P&L.'],
  ['FX revaluation', 'ASC 830', 'Controller', 'Monthly closing-rate remeasurement of Prague monetary items.'],
  ['CTA adjustment', 'ASC 830', 'Senior Accounting', 'Translation adjustment through OCI only.'],
  ['Restructuring reserve', 'ASC 420', 'Board approval', 'Template retained; no current plan.'],
  ['IBNR reserve', 'Actuarial', 'N/A', 'Not applicable while fully insured.'],
];

const decisionSteps = [
  'Load the contingency log and present each active matter to GC.',
  'Collect period-specific GC confirmation; prior month assessments do not carry forward automatically.',
  'Apply ASC 450: probable and estimable items accrue; reasonably possible items disclose.',
  'Route all accrual and disclosure classifications through the required HITL/HOTL gates.',
  'Generate signed workpaper evidence and a NetSuite-ready JE CSV with live gate status.',
];

export default function InstructionsPage() {
  return (
    <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-5">
          <Panel>
            <PanelHeader className="flex items-start justify-between gap-4">
              <div>
                <PanelTitle>EC-02e Operating Instructions</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Judgment and contingency accrual workflow for monthly close.
                </p>
              </div>
              <Badge variant="danger" className="gap-1.5">
                <LockClosedIcon className="h-3.5 w-3.5" />
                Never auto-approve
              </Badge>
            </PanelHeader>
            <PanelBody>
              <div className="grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                <div className="rounded-lg bg-gray-50 p-5 dark:bg-dark-surface-2">
                  <ScaleIcon className="h-7 w-7 text-eliza-red" />
                  <h2 className="mt-4 text-lg font-semibold text-charcoal dark:text-gray-100">Role boundary</h2>
                  <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                    EC-02e is a documentation and workflow orchestration agent. It does not make management decisions; it frames the evidence, applies the accounting framework, and records approvals.
                  </p>
                </div>
                <div className="space-y-3">
                  {decisionSteps.map((step, index) => (
                    <div key={step} className="grid grid-cols-[32px_minmax(0,1fr)] gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-charcoal dark:bg-dark-surface-2 dark:text-gray-100">
                        {index + 1}
                      </div>
                      <p className="pt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Coverage Matrix</PanelTitle>
            </PanelHeader>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium">Item</th>
                    <th className="px-5 py-3 text-left font-medium">Standard</th>
                    <th className="px-5 py-3 text-left font-medium">Input owner</th>
                    <th className="px-5 py-3 text-left font-medium">Treatment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                  {coveredItems.map(([item, standard, owner, treatment]) => (
                    <tr key={item}>
                      <td className="px-5 py-4 font-semibold text-charcoal dark:text-gray-100">{item}</td>
                      <td className="px-5 py-4 text-gray-600 dark:text-gray-300">{standard}</td>
                      <td className="px-5 py-4 text-gray-600 dark:text-gray-300">{owner}</td>
                      <td className="px-5 py-4 text-gray-600 dark:text-gray-300">{treatment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <aside className="space-y-5">
          <Panel>
            <PanelHeader>
              <PanelTitle>Standard Gates</PanelTitle>
            </PanelHeader>
            <PanelBody className="space-y-3">
              {approvalGates.map(gate => (
                <div key={gate.id} className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={gate.type === 'HITL' ? 'danger' : 'info'}>{gate.type}</Badge>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{gate.approver}</span>
                  </div>
                  <div className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">{gate.title}</div>
                  <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{gate.subtitle}</p>
                </div>
              ))}
            </PanelBody>
          </Panel>
        </aside>
      </div>
    </div>
  );
}
